/**
 * `AdsController` — Melodimals' ad orchestration.
 *
 * It is the layer that joins the kit's three pieces and **decides nothing on its
 * own**: `AdProvider` says whether it CAN (SDK, network, fill), `adGate` says whether it is
 * DUE (policy + capping) and `AdCapsStore` keeps the local-first counters.
 *
 * Everything it needs from the world arrives through the constructor (clock, current
 * path, premium flag, storage), so the class is tested whole without a browser, without
 * an SDK and without waiting ten minutes for a cap to expire.
 *
 * ── Regime K, in one sentence ────────────────────────────────────────────────
 * `user.child` is **always `true`**: there is no adult mode in an app for kids
 * aged 2 to 6, so the gate applies `CHILD_AD_CAP_POLICY` (1 interstitial
 * per session, 2 per day) and policy §6 (only `between-screens`, never inside
 * a game, rewarded only after an adult gate) on every call.
 */

import { type AdCapsStorage, AdCapsStore } from "./growth/caps";
import { isInGameRoute } from "./growth/domain";
import { adGate } from "./growth/gate";
import { placementDef } from "./growth/placements";
import type {
  AdCapPolicy,
  AdGateDecision,
  AdProvider,
  AdSessionState,
  PlacementRegistry,
} from "./growth/types";

/**
 * Mark of "this device has already played once". Without it, `isFirstEver` is
 * `true` and the gate denies the interstitial with `first-session` — on purpose:
 * a child's first contact with the game is not burned with an ad.
 */
export const ADS_SESSION_SEEN_KEY = "ads.session.seen";

/**
 * The canon's `AdProvider` contract does not know about the persistent banner (on web a
 * banner is unmounted with its DOM node). On mobile it does need to be removed by
 * hand: it is a NATIVE view that survives WebView navigation. It is
 * declared as an OPTIONAL capability so as not to touch the shared contract.
 */
export type BannerCapableAdProvider = AdProvider & {
  hideBanner?: () => Promise<void>;
};

export interface AdsControllerDeps {
  provider: BannerCapableAdProvider;
  /** Storage for the counters (localStorage in the app, a Map in tests). */
  storage: AdCapsStorage;
  /** The app's placement registry (source of truth of what exists). */
  placements: PlacementRegistry;
  /** Premium ⇒ 0 ads, immediate and without waiting for the network. */
  isPremium: () => boolean;
  /** Current path: if it is a game, no ad is worth it. */
  currentPath: () => string;
  /**
   * Effective capping, READ ON EVERY EVALUATION. It is what allows the adult
   * to request fewer ads from the parents' area and have it applied
   * live, without restarting the game (see `ad-policy.ts`). Absent ⇒ the
   * child policy of `gate.ts` as is.
   */
  policy?: () => Partial<AdCapPolicy>;
  /** Injected clock: the gate is pure and the tests do not really wait. */
  now?: () => number;
}

export class AdsController {
  private readonly caps: AdCapsStore;
  private readonly now: () => number;
  private readonly sessionStartedAt: number;
  private unitsCompleted = 0;
  private readonly isFirstEver: boolean;
  private bannerVisible = false;

  constructor(private readonly deps: AdsControllerDeps) {
    this.now = deps.now ?? Date.now;
    this.sessionStartedAt = this.now();
    this.caps = new AdCapsStore(deps.storage, "device", this.now());

    let seen = false;
    try {
      seen = deps.storage.getItem(ADS_SESSION_SEEN_KEY) === "1";
      deps.storage.setItem(ADS_SESSION_SEEN_KEY, "1");
    } catch {
      // Storage blocked (private mode): first session is assumed, which is the
      // conservative option — fewer ads, never more.
    }
    this.isFirstEver = !seen;
  }

  /** Initializes the SDK. Idempotent; with the `none` network it does nothing. */
  async init(): Promise<void> {
    await this.deps.provider.init();
  }

  /**
   * Preloads the interstitial so that the transition between modules does not wait for
   * the network. Best-effort: without fill it returns `false` and nothing happens — the gate
   * would have let navigation continue anyway.
   */
  async prewarm(): Promise<boolean> {
    try {
      return await this.deps.provider.prepare("interstitial", "between-games");
    } catch {
      return false;
    }
  }

  /**
   * The child finished playing a module: **one unit of value**. It is the
   * §4 requirement for an interstitial to stop being an interruption and
   * become a breather; without this the gate denies with `no-value-unit`.
   */
  recordGamePlayed(): void {
    this.unitsCompleted += 1;
  }

  private session(): AdSessionState {
    return {
      activeMs: Math.max(0, this.now() - this.sessionStartedAt),
      unitsCompleted: this.unitsCompleted,
      isFirstEver: this.isFirstEver,
    };
  }

  /**
   * Gate verdict for a placement, without showing anything. Public because it is
   * what the UI consults to decide whether it RESERVES a slot (and because it returns the
   * `reason`, which is what the tests assert).
   */
  evaluate(placementId: string): AdGateDecision {
    const def = placementDef(this.deps.placements, placementId);
    if (!def) {
      // An undeclared placement does not exist. It is denied with the policy
      // reason so that nobody can "sneak in" a slot without going through the registry.
      return { allow: false, reason: "child-policy" };
    }
    const now = this.now();
    return adGate({
      kind: def.kind,
      placementId,
      breakpoint: def.breakpoint,
      rewardType: def.reward?.type,
      now,
      caps: this.caps.snapshot(now),
      session: this.session(),
      user: {
        premium: this.deps.isPremium(),
        // Melodimals IS the kids' surface: there is no adult user that
        // relaxes the regime. The parental gate gives access to the SETTINGS, not to
        // a different ad policy.
        child: true,
        adultGateCleared: false,
      },
      inFlight: isInGameRoute(this.deps.currentPath()),
      ...(this.deps.policy ? { policy: this.deps.policy() } : {}),
    });
  }

  /**
   * Interstitial between games. Returns `true` only if it was really shown.
   *
   * **It never throws**: it is called in the middle of a navigation, and an ad
   * failure cannot leave a child staring at a screen that does not change.
   */
  async maybeShowInterstitial(placementId = "between-games"): Promise<boolean> {
    const decision = this.evaluate(placementId);
    if (!decision.allow) {
      return false;
    }
    try {
      const { shown } = await this.deps.provider.show("interstitial", placementId);
      if (shown) {
        // It only counts what the user SAW. A `show` without fill does not spend the day's
        // cap: otherwise, a day without inventory would leave the app with no ads and
        // the counters exhausted.
        this.caps.recordInterstitial(this.now());
      }
      return shown;
    } catch {
      return false;
    }
  }

  /**
   * Banner of a passive screen (today only `/progress`). Idempotent: calling it
   * twice does not stack banners — the gate's "1 visible" cap would prevent it,
   * but the screen counter must be maintained by hand because it does not persist.
   */
  async showBanner(placementId = "progress-banner"): Promise<boolean> {
    if (this.bannerVisible) {
      return true;
    }
    const decision = this.evaluate(placementId);
    if (!decision.allow) {
      return false;
    }
    try {
      const { shown } = await this.deps.provider.show("banner", placementId);
      if (shown) {
        this.bannerVisible = true;
        this.caps.setScreenCounts({ banners: 1 });
      }
      return shown;
    } catch {
      return false;
    }
  }

  /** Removes the banner when leaving the passive screen. Always safe. */
  async hideBanner(): Promise<void> {
    this.bannerVisible = false;
    this.caps.setScreenCounts({ banners: 0 });
    try {
      await this.deps.provider.hideBanner?.();
    } catch {
      // Nothing to do: the screen counter is already at zero.
    }
  }

  /**
   * Post-premium purchase: zero ads **immediately**, without waiting for any
   * network confirmation. Removes the live banner and forgets the capping (which no
   * longer means anything for this device).
   */
  async onPremiumAcquired(): Promise<void> {
    await this.hideBanner();
    this.caps.reset(this.now());
  }

  /** Only for tests and debugging: the capping state just as the gate sees it. */
  capsSnapshot() {
    return this.caps.snapshot(this.now());
  }
}
